import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useSlotGroupOverrides } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Schedule A Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function ScheduleATourCallout() {
  const ctaLink = useSlotGroup('scheduleatourcallout.cta_link', ctaLinkDef);
  const slotGroupOverrides = useSlotGroupOverrides();

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-8 md:py-12 bg-primary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={sectionRef} className="reveal-fade-up text-center glow-trace-wrap relative">
          <GlowTrace delay={0.3} />
          <RichTextContent
            path="scheduleatourcallout.title"
            label="Title"
            defaultValue='<p>Ready to See It in Person?</p>'
            as="h2"
            className="font-heading text-3xl sm:text-4xl font-normal text-primary-foreground mb-4 uppercase tracking-wide"
          />

          <RichTextContent
            path="scheduleatourcallout.subtitle"
            label="Subtitle"
            defaultValue='<p>Tour <span data-type="field-token" data-field="property_name"></span> and explore the neighborhood firsthand.</p>'
            as="p"
            className="font-body text-lg text-primary-foreground max-w-2xl mx-auto mb-8"
          />

          <div className="w-16 h-1 mx-auto mb-8 rounded-full bg-primary-foreground/40" />

          <EditableSlotGroup
            groupPath="scheduleatourcallout.cta_link"
            definition={ctaLinkDef}
            override={slotGroupOverrides['scheduleatourcallout.cta_link']}
          >
            <a
              href={ctaLink.url || '/schedule-a-tour'}
              target={ctaLink._target || '_self'}
              className="inline-flex items-center px-8 py-3 text-sm font-semibold tracking-widest uppercase border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              {ctaLink.label || 'Schedule A Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleATourCallout',
  displayName: 'Schedule A Tour Callout',
  tokens: {
    'scheduleatourcallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Ready to See It in Person?</p>',
    },
    'scheduleatourcallout.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Tour <span data-type="field-token" data-field="property_name"></span> and explore the neighborhood firsthand.</p>',
    },
  },
  slotGroups: {
    'scheduleatourcallout.cta_link': ctaLinkDef,
  },
};

(ScheduleATourCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleATourCallout;
